import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"

import {
  DEFAULT_TIMEZONE,
  detectTimezone,
  getStoredTimezone,
  getTimezoneDisplay,
  storeTimezone,
} from "@/lib/timezone"

interface TimezoneContextType {
  /** User's effective timezone (IANA identifier like "America/Los_Angeles") */
  timezone: string
  /** Human-readable timezone display (like "Pacific Time (US & Canada)") */
  timezoneDisplay: string
  /** Update timezone (stores in localStorage) */
  setTimezone: (tz: string) => void
  /** false in simplified browser-driven mode */
  isUserTimezone: boolean
}

const TimezoneContext = createContext<TimezoneContextType | null>(null)

const TIMEZONE_RECHECK_INTERVAL_MS = 12 * 60 * 60 * 1000

export function TimezoneProvider({ children }: { children: ReactNode }) {
  // Keep initial SSR and client render deterministic; hydrate browser timezone after mount.
  const [timezone, setTimezoneState] = useState<string>(DEFAULT_TIMEZONE)

  const isUserTimezone = false

  useEffect(() => {
    if (typeof window !== "undefined") {
      const stored = getStoredTimezone()
      if (stored) {
        setTimezoneState(stored)
      } else {
        setTimezoneState(detectTimezone())
      }
    }
  }, [])

  const setTimezone = useCallback(
    (tz: string) => {
      setTimezoneState(tz)
      storeTimezone(tz)
    },
    []
  )

  const detectTimezoneIfChanged = useCallback(() => {
    if (typeof window === "undefined") return

    const detected = detectTimezone()
    if (!detected || detected === timezone) return

    setTimezoneState(detected)
    storeTimezone(detected)
  }, [timezone])

  useEffect(() => {
    if (typeof window === "undefined") return

    detectTimezoneIfChanged()

    const handleFocus = () => detectTimezoneIfChanged()
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        detectTimezoneIfChanged()
      }
    }

    const intervalId = window.setInterval(
      detectTimezoneIfChanged,
      TIMEZONE_RECHECK_INTERVAL_MS
    )

    window.addEventListener("focus", handleFocus)
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      window.clearInterval(intervalId)
      window.removeEventListener("focus", handleFocus)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [detectTimezoneIfChanged])

  const timezoneDisplay = getTimezoneDisplay(timezone)

  return (
    <TimezoneContext.Provider
      value={{ timezone, timezoneDisplay, setTimezone, isUserTimezone }}
    >
      {children}
    </TimezoneContext.Provider>
  )
}

export function useTimezone() {
  const context = useContext(TimezoneContext)
  if (!context) {
    throw new Error("useTimezone must be used within TimezoneProvider")
  }
  return context
}

/**
 * Hook to get timezone without throwing if provider is not present
 * Useful for components that may be used outside the provider
 */
export function useTimezoneOptional(): TimezoneContextType | null {
  return useContext(TimezoneContext)
}
